import { useQuery } from "@tanstack/react-query";
import { ConsensusMeterResults, getYesNoResults } from "helpers/api";
import { getYesNoEndpointParams } from "helpers/endpointQueryParams";
import { useRouter } from "next/router";
import {
  CategoryAggregations,
  CategoryToWins,
  CategoryWinners,
  ConsensusMeterData,
  MeterCategories,
} from "../types";
import { orderedCategoryAggregations } from "../utils";

export type SearchParams = {
  searchId?: string;
  isProResearch?: boolean;
  disabled?: boolean;
};

type UseConsensusMeterParams = {
  searchParams?: SearchParams;
  queryOverride?: string;
  disabled?: boolean;
};

export const useConsensusMeter = ({
  searchParams,
  queryOverride,
  disabled,
}: UseConsensusMeterParams) => {
  const router = useRouter();
  const baseParams = getYesNoEndpointParams(router.query);

  const { searchId, isProResearch } = searchParams ?? {};

  // Add the new parameters to query params
  const params = new URLSearchParams(baseParams);
  if (queryOverride) {
    params.set("query_override", queryOverride);
  }
  const finalParams = params.toString();

  const query = useQuery<ConsensusMeterData>(
    ["consensus-meter", searchId],
    async () => {
      const results = await getYesNoResults(
        { threadSearchId: searchId! },
        finalParams
      );
      return { ...results, ...calculateCategoryWinners(results) };
    },
    {
      enabled: !disabled && !!searchId && !(isProResearch && !queryOverride),
      staleTime: Infinity,
      retry: (failureCount) => {
        return failureCount < 3;
      },
    }
  );

  // Semantic variables instead of yesNoMeterSkippedOrIncomplete
  const isIncomplete = query.data?.is_incomplete ?? false;
  const isSkipped = query.data?.is_skipped ?? false;
  const isDisputed = query.data?.is_disputed ?? false;
  const hasMeterFailed =
    !query.isLoading && query.isSuccess && (isIncomplete || isSkipped);

  return {
    consensusMeterSuccess: query.isSuccess,
    consensusMeterResults: query.data,
    isLoading: query.isLoading,
    // New semantic variables
    hasMeterFailed,
    isIncomplete,
    isSkipped,
    isDisputed,
  };
};

const calculateCategoryWinners = (
  consensusMeterResults: ConsensusMeterResults
): CategoryWinners => {
  let categoryToWins: CategoryToWins = {
    yes: [],
    no: [],
    possibly: [],
    mixed: [],
  };
  const aggregationTies: CategoryAggregations[] = [];
  if (consensusMeterResults.is_incomplete) {
    return { categoryToWins, aggregationTies };
  }

  const categories: MeterCategories[] = ["yes", "no", "possibly", "mixed"];

  for (const aggregation of orderedCategoryAggregations) {
    const values: { category: MeterCategories; value: number }[] = [];

    for (const category of categories) {
      if (consensusMeterResults[category]) {
        // Skip journal_score if it's 0
        if (
          aggregation === "journal_score" &&
          consensusMeterResults[category]![aggregation] === 0
        ) {
          continue;
        }
        values.push({
          category,
          value: consensusMeterResults[category]![aggregation],
        });
      }
    }

    // If there's only one value, skip it
    if (values.length <= 1) {
      continue;
    }

    const sortedValues = values.sort((a, b) => {
      if (aggregation === "journal_score") {
        return a.value - b.value;
      } else {
        return b.value - a.value;
      }
    });

    const winningValue = sortedValues[0].value;
    const winners = sortedValues.filter((v) => v.value === winningValue);

    // If number of winners is less than number of values, they're all tied.
    if (winners.length < values.length) {
      for (const winner of winners) {
        categoryToWins[winner.category].push(aggregation);
      }

      if (winners.length > 1) {
        aggregationTies.push(aggregation);
      }
    }
  }

  return { categoryToWins, aggregationTies };
};
